import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { Button, Empty, Modal, Skeleton, message } from 'antd';
import { CameraOutlined, RightOutlined, UpOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useLazyQuery, useMutation } from '@apollo/client';
import ColumnHeader from './ColumnHeader';
import TaskPhotoProofModal from './TaskPhotoProofModal';
import {
  composeCountText,
  composeTaskSubtitle,
  composeTaskTitle,
  composeWindowText,
  isAsapTask,
} from '../utils/taskTextComposer';
import { WMS_DEPARTURE_REVIEW_PATH, WMS_NO_UPDATE_WAREHOUSE_PATH } from '../../routePaths';
import { COMPLETE_WORKBENCH_TASK } from '../graphql/mutations/workbenchMutations';
import { WORKBENCH_FINISHED_TASKS, WORKBENCH_PAGE_SIZE } from '../graphql/queries/workbenchBoard';

/**
 * 需要仓管手动勾选完结的任务类型（PRD「人工完结」三条）。其余类型由系统按作业数据自动完结，
 * 给它们渲染勾选框会让仓管以为需要自己点。
 *
 * 不用上游 `canComplete` 判断：它当前由 api-server 本地按「非终态」推导（plan D-1），
 * 对系统完结类任务同样返回 true。「是否人工完结」是任务类型的固有属性，按 taskCode 派生即可；
 * 上游哪天真下发 `canComplete` 再改用上游值。
 */
const MANUAL_COMPLETION_TASK_CODES = new Set([
  'SOP_LINEHAUL_CHECKIN',
  'SOP_RETURN_LOADOUT',
  'SOP_SITE_CLEARANCE',
]);

/**
 * hub 派发的任务（`source = 'ASSIGNED'`，对应上游 `CategoryType.MANUAL_ASSIGN`）标题是自由文本，
 * 系统没有任何作业数据能判定它做完了没有，只能由仓管自己勾选，故不看 taskCode。
 */
const isManualCompletionTask = (task: WorkbenchTask): boolean =>
  task.source === 'ASSIGNED' || (!!task.taskCode && MANUAL_COMPLETION_TASK_CODES.has(task.taskCode));

/**
 * 任务标题的落地页。仓管要逐单看才能处理的任务才给链接，其余出纯文本 ——
 * 跳到猜的页面比不跳更糟。跳转页的仓取自 WarehouseContext，与工作台同源，不必带 warehouseId。
 */
const DETAIL_PATH_BY_TASK_CODE: Record<string, string> = {
  SOP_EXCEPTION_NO_UPDATE: WMS_NO_UPDATE_WAREHOUSE_PATH,
  SOP_DRIVER_DEPART_AUDIT: WMS_DEPARTURE_REVIEW_PATH,
};

const detailPathOf = (taskCode?: string | null): string | undefined =>
  (taskCode ? DETAIL_PATH_BY_TASK_CODE[taskCode] : undefined);

/** 上游 AttachType：清场任务有专属类型，其余走通用人工留证 */
const attachTypeOf = (taskCode?: string | null): string =>
  (taskCode === 'SOP_SITE_CLEARANCE' ? 'SITE_CLEARANCE' : 'PHOTO_PROOF');

export interface WorkbenchTask {
  taskId: number;
  source: string;
  taskCode?: string | null;
  title?: string | null;
  detail?: string | null;
  category: string;
  status: string;
  priority: string;
  isOverdue: boolean;
  overdueMinutes?: number | null;
  windowStartAt?: string | null;
  windowEndAt?: string | null;
  progressCurrent?: number | null;
  progressTotal?: number | null;
  exceptionCount?: number | null;
  exceptionRoute?: string | null;
  actionCode?: string | null;
  actionRoute?: string | null;
  howToStepCount: number;
  requirePhotoProof: boolean;
  completedAt?: string | null;
  completedByName?: string | null;
  canComplete: boolean;
  canReopen: boolean;
}

interface TasksSectionProps {
  data: {
    warehouseId: number;
    taskDate: string;
    serverNow: string;
    doneCount: number;
    totalCount: number;
    completionPercent: number;
    tasks: WorkbenchTask[];
    doneTasks: WorkbenchTask[];
  };
  /** 仓所在 IANA 时区，来自 board；为空时下游时间文案降级为 — */
  timeZone?: string | null;
  warehouseId?: number;
  loading?: boolean;
  onTaskComplete?: () => void;
}

/**
 * 指引步骤全部来自前端 i18n。不用上游 `howToStepCount` 判断有无指引 ——
 * 两边不一致时会出现「有指引入口、点开却是空」。
 */
const guideSteps = (t: TFunction, task: WorkbenchTask): string[] => {
  if (!task.taskCode) return [];
  const steps = t(`workbench.tasks.${task.taskCode}.steps`, { returnObjects: true }) as unknown;
  return Array.isArray(steps) ? (steps as string[]) : [];
};

/**
 * 人工完结任务的「去完成」按钮。置顶卡与待办行共用一份完结逻辑：
 * 置顶卡是排在首位那条任务的唯一渲染处，没有入口就永远完不成（到车签到恒被 pin 到首位）。
 */
const TaskCompleteButton: React.FC<{
  task: WorkbenchTask;
  warehouseId?: number;
  onTaskComplete?: () => void;
}> = ({ task, warehouseId, onTaskComplete }) => {
  const { t } = useTranslation();
  const [photoModalOpen, setPhotoModalOpen] = useState(false);
  const [completeMutation] = useMutation(COMPLETE_WORKBENCH_TASK);

  const titleText = composeTaskTitle(task.taskCode, task.title);

  // 照片凭证类任务（清点到货、清场等）必须先上传照片才能完成，故走弹窗而非直接勾选。
  // photoS3Keys 是前端直传私有桶后拿到的对象键，原样透传给上游，不传文件本体
  const completeTask = async (photoS3Keys: string[]) => {
    if (!warehouseId) return;
    try {
      const { data: result } = await completeMutation({
        variables: {
          warehouseId,
          taskId: task.taskId,
          photoS3Keys,
          comment: null,
          attachType: photoS3Keys.length > 0 ? attachTypeOf(task.taskCode) : null,
        },
      });
      // 上游对重复提交做状态守卫返回 false，不是错误
      if (result?.completeWorkbenchTask) {
        message.success(String(t('workbench.taskCompleted')));
      } else {
        message.info(String(t('workbench.alertText.alreadyHandled')));
      }
      onTaskComplete?.();
    } catch (e) {
      message.error(String(t('workbench.alertText.handleFailed')));
    }
  };

  const handleToggleComplete = () => {
    if (!task.canComplete) {
      message.warning(String(t('workbench.cannotComplete')));
      return;
    }
    if (task.requirePhotoProof) {
      setPhotoModalOpen(true);
      return;
    }
    // 上游不支持反勾（canReopen 恒为否），误点一下就永久完结，故加一次确认。
    // 需留证的任务本身要走上传弹窗，已是二次确认，不重复拦
    Modal.confirm({
      title: String(t('workbench.confirmCompleteTitle', { name: titleText })),
      content: String(t('workbench.confirmCompleteContent')),
      okText: String(t('common.confirm')),
      cancelText: String(t('common.cancel')),
      onOk: () => completeTask([]),
    });
  };

  // 已完成的任务不再给完结入口：上游不支持反勾（canReopen 恒为否），
  // 留着只会让仓管点出一句「无法完成该任务」
  if (!isManualCompletionTask(task) || task.status === 'DONE') {
    return null;
  }

  return (
    <>
      <Button
        size="small"
        type="primary"
        disabled={!task.canComplete}
        onClick={handleToggleComplete}
      >
        {String(t('workbench.goComplete'))}
      </Button>
      <TaskPhotoProofModal
        open={photoModalOpen}
        taskTitle={titleText}
        requirePhotoProof={task.requirePhotoProof}
        warehouseId={warehouseId}
        taskId={task.taskId}
        onCancel={() => setPhotoModalOpen(false)}
        onConfirm={(photoS3Keys) => {
          setPhotoModalOpen(false);
          completeTask(photoS3Keys);
        }}
      />
    </>
  );
};

const TaskRow: React.FC<{
  task: WorkbenchTask;
  index: number;
  timeZone?: string | null;
  warehouseId?: number;
  onTaskComplete?: () => void;
  guideExpanded: boolean;
  onToggleGuide: (taskId: number) => void;
}> = ({ task, index, timeZone, warehouseId, onTaskComplete, guideExpanded, onToggleGuide }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  // 已完成任务只能看留证，不能再传：复用同一个弹窗，用只读态区分
  const [photoViewOpen, setPhotoViewOpen] = useState(false);

  const canViewPhotos = task.status === 'DONE' && task.requirePhotoProof;

  const titleText = composeTaskTitle(task.taskCode, task.title);
  const subtitleText = composeTaskSubtitle(task.taskCode, task.detail);
  const detailPath = detailPathOf(task.taskCode);

  const stepList = guideSteps(t, task);

  const isUrgent = task.isOverdue || isAsapTask(task.windowEndAt);
  // 「去完成」按钮的出现口径与原勾选框一致：仅人工完结类任务、且未完成
  const showCompleteButton = isManualCompletionTask(task) && task.status !== 'DONE';

  return (
    <>
      <tr className={isUrgent ? 'bg-red-50/50' : undefined}>
        <td className="py-2.5 pl-3 pr-2">
          <p className="text-sm font-medium text-gray-900">
            <span className="text-gray-400 mr-2">{index}</span>
            {detailPath ? (
              <button
                type="button"
                className="text-violet-600 underline hover:text-violet-700"
                onClick={() => navigate(detailPath)}
              >
                {titleText}
              </button>
            ) : (
              titleText
            )}
            {task.requirePhotoProof && (
              canViewPhotos ? (
                <button
                  type="button"
                  className="ml-1.5 text-violet-600"
                  title={String(t('workbench.viewPhotoProof'))}
                  onClick={() => setPhotoViewOpen(true)}
                >
                  <CameraOutlined />
                </button>
              ) : (
                <CameraOutlined
                  className="ml-1.5 text-gray-400"
                  title={String(t('workbench.photoRequired'))}
                />
              )
            )}
          </p>
          {subtitleText && <p className="text-xs text-gray-500 mt-0.5">{subtitleText}</p>}
          {/* 「去完成」按钮放在标题/副标题下方，独占一行；不要挤到序号前 */}
          {showCompleteButton && (
            <div className="mt-1.5">
              <TaskCompleteButton
                task={task}
                warehouseId={warehouseId}
                onTaskComplete={onTaskComplete}
              />
            </div>
          )}
        </td>
        <td className="py-2.5 pr-2 text-sm text-gray-700 text-right">
          {/* 手动派发任务系统侧无作业数据可采集，count 无语义，直接出 — */}
          {task.source === 'ASSIGNED'
            ? '—'
            : (composeCountText(task.progressCurrent, task.progressTotal, task.taskCode) || '—')}
        </td>
        <td
          className={`py-2.5 pr-2 text-sm ${isUrgent ? 'text-red-500 font-medium' : 'text-gray-700'}`}
        >
          {composeWindowText(task.windowEndAt, timeZone)}
        </td>
        <td className="py-2.5 pr-3 text-sm whitespace-nowrap text-right">
          {stepList.length > 0 && (
            <button
              type="button"
              className="text-violet-600 hover:underline"
              onClick={() => onToggleGuide(task.taskId)}
            >
              {String(t('workbench.guide'))}
              {guideExpanded ? <UpOutlined className="ml-0.5" /> : <RightOutlined className="ml-0.5" />}
            </button>
          )}
        </td>
      </tr>
      {stepList.length > 0 && guideExpanded && (
        <tr className={isUrgent ? 'bg-red-50/50' : undefined}>
          <td colSpan={4} className="pb-3 pl-3 pr-3">
            <ol className="space-y-1.5">
              {stepList.map((step, idx) => (
                <li key={idx} className="flex gap-2 text-sm text-gray-700">
                  <span className="shrink-0 w-4 h-4 rounded bg-violet-100 text-violet-600 text-[10px] flex items-center justify-center font-semibold mt-0.5">
                    {idx + 1}
                  </span>
                  <span className="whitespace-pre-line">{step}</span>
                </li>
              ))}
            </ol>
          </td>
        </tr>
      )}
      <TaskPhotoProofModal
        open={photoViewOpen}
        readOnly
        taskTitle={titleText}
        requirePhotoProof={task.requirePhotoProof}
        warehouseId={warehouseId}
        taskId={task.taskId}
        onCancel={() => setPhotoViewOpen(false)}
        onConfirm={() => setPhotoViewOpen(false)}
      />
    </>
  );
};

const TasksSection: React.FC<TasksSectionProps> = ({
  data,
  timeZone,
  warehouseId,
  loading,
  onTaskComplete,
}) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('todo');
  // 每个任务的指引展开态：默认全收起；点击每行「指引」按钮或右上角「展开全部指引」切换
  const [expandedTaskIds, setExpandedTaskIds] = useState<Set<number>>(new Set());
  const toggleGuide = useCallback((taskId: number) => {
    setExpandedTaskIds((prev) => {
      const next = new Set(prev);
      if (next.has(taskId)) next.delete(taskId);
      else next.add(taskId);
      return next;
    });
  }, []);
  // 「已完成」独立分页：首屏 board 只带上游首段，翻页一律走 workbenchFinishedTasks
  const [donePages, setDonePages] = useState<WorkbenchTask[] | null>(null);
  const [doneHasMore, setDoneHasMore] = useState(false);
  const [doneNextPage, setDoneNextPage] = useState(1);
  const [fetchDone, { loading: doneLoading }] = useLazyQuery(WORKBENCH_FINISHED_TASKS, {
    fetchPolicy: 'network-only',
  });

  const loadDonePage = useCallback(
    async (pageNum: number) => {
      if (!warehouseId) return;
      const res = await fetchDone({
        variables: { warehouseId, pageNum, pageSize: WORKBENCH_PAGE_SIZE },
      });
      const page = res.data?.workbenchFinishedTasks;
      if (!page) return;
      setDonePages((prev) => (pageNum === 1 ? page.items : [...(prev ?? []), ...page.items]));
      setDoneHasMore(page.hasMore);
      setDoneNextPage(pageNum + 1);
    },
    [fetchDone, warehouseId]
  );

  // 完结一条任务后必须把已缓存的「已完成」分页作废：它是独立分页的结果，
  // board 重新拉取不会更新它，否则新完结的任务要等用户手动刷新页面才出现
  const handleTaskComplete = useCallback(() => {
    setDonePages(null);
    setDoneHasMore(false);
    setDoneNextPage(1);
    // 正停在「已完成」页时立刻重取，不能等下次切 tab
    if (activeTab === 'done') {
      loadDonePage(1);
    }
    onTaskComplete?.();
  }, [activeTab, loadDonePage, onTaskComplete]);

  const handleTabChange = useCallback(
    (key: string) => {
      setActiveTab(key);
      if (key === 'done' && donePages === null) {
        loadDonePage(1);
      }
    },
    [donePages, loadDonePage]
  );

  if (loading) {
    return <Skeleton active paragraph={{ rows: 8 }} />;
  }

  // 待办列表按后端返回的顺序展示（sortNo 全局排，进行中不再硬置顶）
  const todoRows = data.tasks;
  const todoCount = data.tasks.length;
  const isTodo = activeTab === 'todo';

  // 分页结果未回来前先用 board 首段，避免切 tab 瞬间空列表
  const doneRows = donePages ?? data.doneTasks;
  const rows = isTodo ? todoRows : doneRows;
  // 「全部指引」在待办与已完成两个 tab 的行上都生效：切 tab 后仍保留展开态，
  // 不用重新点
  const expandableTaskIds = [...todoRows, ...doneRows]
    .filter((task) => guideSteps(t, task).length > 0)
    .map((task) => task.taskId);
  const allGuidesExpanded =
    expandableTaskIds.length > 0 && expandableTaskIds.every((id) => expandedTaskIds.has(id));
  const toggleAllGuides = () => {
    setExpandedTaskIds(allGuidesExpanded ? new Set() : new Set(expandableTaskIds));
  };

  return (
    <div className="border border-violet-200 rounded-lg flex flex-col">
      <ColumnHeader
        activeKey={activeTab}
        onChange={handleTabChange}
        tabs={[
          { key: 'todo', label: String(t('workbench.iNeedToDo')), count: todoCount },
          { key: 'done', label: String(t('workbench.done')), count: data.doneCount },
        ]}
        extra={
          expandableTaskIds.length > 0 && (
            <button
              type="button"
              className="text-violet-600 hover:underline"
              onClick={toggleAllGuides}
            >
              {String(t(allGuidesExpanded ? 'workbench.collapseAllGuides' : 'workbench.expandAllGuides'))}
            </button>
          )
        }
      />

      {/* 列表最高占七成视口，超出本栏内滚，不影响整页滚动 */}
      <div className="p-4 space-y-4 max-h-[70vh] overflow-y-auto">
        {rows.length > 0 ? (
          <div className="border border-violet-200 rounded-lg overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr className="text-xs text-gray-500">
                  <th className="py-2 pl-3 pr-2 text-left font-normal">
                    {String(t(isTodo ? 'workbench.task' : 'workbench.completedTasks'))}
                  </th>
                  <th className="py-2 pr-2 text-right font-normal">
                    {String(t('workbench.quantity'))}
                  </th>
                  <th className="py-2 pr-2 text-left font-normal">
                    {String(t('workbench.deadline'))}
                  </th>
                  <th className="py-2 pr-3 text-right font-normal">
                    {String(t('workbench.guide'))}
                  </th>
                </tr>
              </thead>
              {/* 每个任务用独立 tbody 包起来（含主行 + 指引展开行），
                  分隔线画在 tbody 之间；否则单个 tbody + divide-y 会把主行和指引行之间也画一条 */}
              {rows.map((task, idx) => (
                <tbody
                  key={task.taskId}
                  className={idx > 0 ? 'border-t border-violet-100' : undefined}
                >
                  <TaskRow
                    task={task}
                    index={idx + 1}
                    timeZone={timeZone}
                    warehouseId={warehouseId}
                    onTaskComplete={handleTaskComplete}
                    guideExpanded={expandedTaskIds.has(task.taskId)}
                    onToggleGuide={toggleGuide}
                  />
                </tbody>
              ))}
            </table>
          </div>
        ) : (
          <Empty description={String(t(isTodo ? 'workbench.noTasks' : 'workbench.noDoneTasks'))} />
        )}

        {!isTodo && doneHasMore && (
          <div className="text-center">
            <Button type="link" loading={doneLoading} onClick={() => loadDonePage(doneNextPage)}>
              {String(t('workbench.loadMore'))}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default TasksSection;
